import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { pluralize } from "@common/helpers/string";
import { FiltersTypes } from "@common/types/filters/general";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import {
    getSuggestedAccrualMatchBills,
    getSuggestedAccrualMatchInvoices,
    getSuggestedAccrualMatchTransactions,
    getTransactions,
} from "@api/transaction.api";
import { getSuggestedAccrualMatchTransactionsForInvoice } from "@api/invoice.api";
import { useScope } from "@state/scopeContext.lib";
import {
    AccrualMatchSortValue,
    AccrualMatchSearchFooterState,
    DEFAULT_ACCRUAL_MATCH_SORT,
    MatchableItem,
    MatchItemType,
} from "./types";
import {
    transactionToMatchableItem,
    invoiceToMatchableItem,
    billToMatchableItem,
} from "./mappers";
import { AccrualMatchTable } from "./AccrualMatchTable";
import { AccrualMatchColumnSettings } from "./AccrualMatchColumnSettings";
import { useAccrualMatch } from "./useAccrualMatch";
import {
    accrualMatchSortToBillSort,
    accrualMatchSortToInvoiceSort,
    accrualMatchSortToTransactionSort,
} from "./accrualMatchSort";
import {
    AccrualMatchSearchMode,
    SearchMatchTypeOption,
    clampToOfferedType,
} from "./matchDrawerDefaults";
import {
    DocumentAnchoredSearch,
    toInvoiceSuggestionFilters,
    useDocumentAnchoredSearchFilters,
} from "./useDocumentAnchoredSearchFilters";
import tableStyles from "./AccrualMatchTable.module.scss";

const SEARCH_MODE_OPTIONS = [
    { value: AccrualMatchSearchMode.ALL, label: "All" },
    { value: AccrualMatchSearchMode.SUGGESTED, label: "Suggested" },
];

const TRANSACTION_FILTERS = [
    FilterTabs.DATE,
    FilterTabs.CATEGORY,
    FilterTabs.ACCOUNT,
    FilterTabs.CLASS,
    FilterTabs.AMOUNT,
    FilterTabs.COUNTERPARTY,
];

const INVOICE_FILTERS = [
    FilterTabs.INVOICE_STATUSES,
    FilterTabs.OVERDUE,
    FilterTabs.AMOUNT,
    FilterTabs.ACCOUNT,
    FilterTabs.SUBSCRIPTION,
];

const BILL_FILTERS = [
    FilterTabs.BILL_STATUSES,
    FilterTabs.DUE,
    FilterTabs.AMOUNT,
    FilterTabs.ACCOUNT,
];

function getFiltersForType(type: MatchItemType): FilterTabs[] {
    switch (type) {
        case MatchItemType.TRANSACTION:
            return TRANSACTION_FILTERS;
        case MatchItemType.INVOICE:
            return INVOICE_FILTERS;
        case MatchItemType.BILL:
            return BILL_FILTERS;
        default:
            return [];
    }
}

const PAGE_LIMIT = 20;

interface Props {
    defaultType: MatchItemType;
    defaultSearchMode: AccrualMatchSearchMode;
    /**
     * Offered match types, derived once by the parent (which also clamps
     * `defaultType` to them) so the selector and the default can't disagree.
     */
    searchMatchTypeOptions: SearchMatchTypeOption[];
    documentAnchoredSearch?: DocumentAnchoredSearch;
    renderFooter: (state: AccrualMatchSearchFooterState) => React.ReactNode;
}

export const SearchMatchesSection: React.FC<Props> = ({
    defaultType,
    defaultSearchMode,
    searchMatchTypeOptions,
    documentAnchoredSearch,
    renderFooter,
}) => {
    const {
        addItem,
        selectedItemIds,
        currentTxLineItemMatchAmounts,
        sourceTransaction,
        isCashBasis,
    } = useAccrualMatch();
    const { workspaceId } = useScope();

    const [searchType, setSearchType] = useState<MatchItemType>(defaultType);
    const [searchMode, setSearchMode] =
        useState<AccrualMatchSearchMode>(defaultSearchMode);
    const [filters, setFilters] = useState<Partial<FiltersTypes>>({});
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState<AccrualMatchSortValue>(
        DEFAULT_ACCRUAL_MATCH_SORT,
    );

    const {
        transactionFilters: transactionsAllFilters,
        suggestedForInvoiceId,
        isSearchModePickerVisible,
    } = useDocumentAnchoredSearchFilters({
        documentAnchoredSearch,
        sourceTransactionAmount: sourceTransaction?.amount ?? 0,
        filters,
    });

    // `defaultType` is derived from the chart of accounts and the billing
    // plan, both fetched asynchronously, so it can resolve after the first
    // render. Adopt the resolved value while the user has not interacted yet
    // (remounting on change instead would discard a typed query mid-search).
    // Picking a match counts as interacting: switching the tab out from under
    // someone who is already working through a result list is worse than
    // leaving them on the tab they started on.
    const hasUserInteractedRef = useRef(false);
    useEffect(() => {
        if (!hasUserInteractedRef.current) {
            setSearchType(defaultType);
            setPage(1);
            setSort(DEFAULT_ACCRUAL_MATCH_SORT);
        }
    }, [defaultType]);

    // The offered set narrows asynchronously (billing entitlement and ledger
    // basis both resolve after mount), and the effect above stops adopting
    // `defaultType` once the user interacts — so without this the user can be
    // stranded on a tab that is no longer offered, with the selector hidden.
    useEffect(() => {
        setSearchType((current) =>
            clampToOfferedType(current, searchMatchTypeOptions),
        );
    }, [searchMatchTypeOptions]);

    const handleFiltersChange = useCallback<FiltersChangeFn>((update) => {
        hasUserInteractedRef.current = true;
        setFilters((prev) => ({ ...prev, ...update }));
        setPage(1);
    }, []);

    const handleTypeChange = useCallback((type: MatchItemType | null) => {
        if (!type) {
            return;
        }
        hasUserInteractedRef.current = true;
        setSearchType(type);
        setFilters({});
        setPage(1);
        setSort(DEFAULT_ACCRUAL_MATCH_SORT);
    }, []);

    const handleSearchModeChange = useCallback(
        (mode: AccrualMatchSearchMode | null) => {
            if (!mode) {
                return;
            }
            hasUserInteractedRef.current = true;
            setSearchMode(mode);
            if (
                mode === AccrualMatchSearchMode.SUGGESTED &&
                searchType === MatchItemType.TRANSACTION &&
                suggestedForInvoiceId !== undefined
            ) {
                setFilters(toInvoiceSuggestionFilters);
            }
            setPage(1);
        },
        [searchType, suggestedForInvoiceId],
    );

    const handleSortChange = useCallback((value: AccrualMatchSortValue) => {
        hasUserInteractedRef.current = true;
        setSort(value);
        setPage(1);
    }, []);

    const handlePageChange = useCallback((nextPage: number) => {
        hasUserInteractedRef.current = true;
        setPage(nextPage);
    }, []);

    const isSuggestedMode = searchMode === AccrualMatchSearchMode.SUGGESTED;
    const isDocumentSearch =
        searchType === MatchItemType.INVOICE ||
        searchType === MatchItemType.BILL;
    const isInvoiceTransactionSuggestions =
        isSuggestedMode &&
        searchType === MatchItemType.TRANSACTION &&
        suggestedForInvoiceId !== undefined;
    // Suggested results are always returned in ranked order, so the
    // user-controlled sort UI is only enabled for browse ("All") mode and for
    // the transactions search (which still uses the regular transactions API).
    const isRankedSearch =
        (isSuggestedMode && isDocumentSearch) ||
        isInvoiceTransactionSuggestions;
    const isEntityFilterPinned =
        isCashBasis && searchType === MatchItemType.BILL;

    // The invoice suggestions endpoint only narrows by search and entity, so
    // the other filter tabs would be silently ignored in that mode.
    const availableFilters = useMemo(
        () =>
            isInvoiceTransactionSuggestions
                ? []
                : getFiltersForType(searchType),
        [searchType, isInvoiceTransactionSuggestions],
    );

    // Cash-basis matching books a sibling entity's invoice to the Due to/from
    // account, but the backend still rejects a bill owned by another entity.
    // Pin the bill search to the transaction's entity (and lock the entity
    // filter below) instead of offering results that can only fail on submit.
    const billEntityIds = useMemo(() => {
        if (!isCashBasis || !sourceTransaction) {
            return filters.entityIds;
        }
        return [sourceTransaction.entity.id];
    }, [isCashBasis, filters.entityIds, sourceTransaction]);

    // `EntityFilterCombobox` in filter mode renders an empty `entityIds` as
    // "every entity selected", so while the pin is in effect it has to be
    // shown the pinned entity — otherwise the control claims a scope the
    // request does not use.
    const entityFilterValue = useMemo(
        () =>
            isEntityFilterPinned
                ? { ...filters, entityIds: billEntityIds }
                : filters,
        [isEntityFilterPinned, filters, billEntityIds],
    );

    const invoiceFilters = useMemo(
        () => ({
            search: filters.search,
            entityIds: filters.entityIds,
            invoiceStatuses: filters.invoiceStatuses,
            overdue: filters.overdue,
            minOverdue: filters.minOverdue,
            maxOverdue: filters.maxOverdue,
            minAmount: filters.minAmount,
            maxAmount: filters.maxAmount,
            accountIds: filters.accountIds,
            isSubscription: filters.isSubscription,
        }),
        [filters],
    );

    const billFilters = useMemo(
        () => ({
            search: filters.search,
            entityIds: billEntityIds,
            billStatuses: filters.billStatuses,
            overdue: filters.overdue,
            minDue: filters.minDue,
            maxDue: filters.maxDue,
            minAmount: filters.minAmount,
            maxAmount: filters.maxAmount,
            accountIds: filters.accountIds,
        }),
        [filters, billEntityIds],
    );

    const transactionsQuery = useQuery({
        queryKey: [
            "accrual-match-transactions",
            workspaceId,
            transactionsAllFilters,
            page,
            sort,
        ],
        queryFn: () =>
            getTransactions({
                workspaceId: workspaceId!,
                filters: transactionsAllFilters,
                page: page - 1,
                limit: PAGE_LIMIT,
                sort: accrualMatchSortToTransactionSort(sort),
            }),
        enabled:
            !isSuggestedMode &&
            searchType === MatchItemType.TRANSACTION &&
            !!workspaceId,
        placeholderData: keepPreviousData,
    });

    const invoicesQuery = useQuery({
        queryKey: [
            "accrual-match-invoices",
            sourceTransaction?.id,
            invoiceFilters,
            page,
            sort,
        ],
        queryFn: async () => {
            const transactionId = sourceTransaction?.id;
            if (!transactionId) {
                throw new Error("Source transaction is required");
            }
            return await getSuggestedAccrualMatchInvoices({
                transactionId,
                query: {
                    filters: invoiceFilters,
                    sort: accrualMatchSortToInvoiceSort(sort),
                    page: page - 1,
                    limit: PAGE_LIMIT,
                    scope: "all",
                },
            });
        },
        enabled:
            !isSuggestedMode &&
            searchType === MatchItemType.INVOICE &&
            !!sourceTransaction?.id,
        placeholderData: keepPreviousData,
    });

    const billsQuery = useQuery({
        queryKey: [
            "accrual-match-bills",
            sourceTransaction?.id,
            billFilters,
            page,
            sort,
        ],
        queryFn: async () => {
            const transactionId = sourceTransaction?.id;
            if (!transactionId) {
                throw new Error("Source transaction is required");
            }
            return await getSuggestedAccrualMatchBills({
                transactionId,
                query: {
                    filters: billFilters,
                    sort: accrualMatchSortToBillSort(sort),
                    page: page - 1,
                    limit: PAGE_LIMIT,
                    scope: "all",
                },
            });
        },
        enabled:
            !isSuggestedMode &&
            searchType === MatchItemType.BILL &&
            !!sourceTransaction?.id,
        placeholderData: keepPreviousData,
    });

    const suggestedTransactionsQuery = useQuery({
        queryKey: [
            "accrual-match-suggested-transactions",
            sourceTransaction?.id,
            filters,
            sort,
        ],
        queryFn: () => {
            const transactionId = sourceTransaction?.id;
            if (!transactionId) {
                throw new Error("Source transaction is required");
            }
            return getSuggestedAccrualMatchTransactions({
                transactionId,
                filters,
                sort: accrualMatchSortToTransactionSort(sort),
            });
        },
        enabled:
            isSuggestedMode &&
            searchType === MatchItemType.TRANSACTION &&
            !!sourceTransaction?.id,
        placeholderData: keepPreviousData,
    });

    const suggestedInvoiceTransactionsQuery = useQuery({
        queryKey: [
            "accrual-match-suggested-transactions",
            "invoice",
            workspaceId,
            suggestedForInvoiceId,
            filters.search,
            filters.entityIds,
            page,
        ],
        queryFn: () => {
            if (!workspaceId || suggestedForInvoiceId === undefined) {
                throw new Error("Workspace and invoice are required");
            }
            return getSuggestedAccrualMatchTransactionsForInvoice({
                workspaceId,
                invoiceId: suggestedForInvoiceId,
                query: {
                    filters: toInvoiceSuggestionFilters(filters),
                    page: page - 1,
                    limit: PAGE_LIMIT,
                },
            });
        },
        enabled: isInvoiceTransactionSuggestions && !!workspaceId,
        placeholderData: keepPreviousData,
    });

    const activeSuggestedTransactionsQuery =
        suggestedForInvoiceId !== undefined
            ? suggestedInvoiceTransactionsQuery
            : suggestedTransactionsQuery;

    const suggestedInvoicesQuery = useQuery({
        queryKey: [
            "accrual-match-suggested-invoices",
            sourceTransaction?.id,
            invoiceFilters,
            page,
        ],
        queryFn: () => {
            const transactionId = sourceTransaction?.id;
            if (!transactionId) {
                throw new Error("Source transaction is required");
            }
            return getSuggestedAccrualMatchInvoices({
                transactionId,
                query: {
                    filters: invoiceFilters,
                    page: page - 1,
                    limit: PAGE_LIMIT,
                    scope: "suggested",
                },
            });
        },
        enabled:
            isSuggestedMode &&
            searchType === MatchItemType.INVOICE &&
            !!sourceTransaction?.id,
        placeholderData: keepPreviousData,
    });

    const suggestedBillsQuery = useQuery({
        queryKey: [
            "accrual-match-suggested-bills",
            sourceTransaction?.id,
            billFilters,
            page,
        ],
        queryFn: () => {
            const transactionId = sourceTransaction?.id;
            if (!transactionId) {
                throw new Error("Source transaction is required");
            }
            return getSuggestedAccrualMatchBills({
                transactionId,
                query: {
                    filters: billFilters,
                    page: page - 1,
                    limit: PAGE_LIMIT,
                    scope: "suggested",
                },
            });
        },
        enabled:
            isSuggestedMode &&
            searchType === MatchItemType.BILL &&
            !!sourceTransaction?.id,
        placeholderData: keepPreviousData,
    });

    const data = useMemo<MatchableItem[]>(() => {
        let items: MatchableItem[] = [];

        switch (searchType) {
            case MatchItemType.TRANSACTION: {
                const transactions = isSuggestedMode
                    ? (activeSuggestedTransactionsQuery.data?.data ?? [])
                    : (transactionsQuery.data?.data ?? []);
                items = transactions.map((tx) =>
                    transactionToMatchableItem({
                        transaction: tx,
                        isCashBasis,
                    }),
                );
                break;
            }
            case MatchItemType.INVOICE: {
                const invoices = isSuggestedMode
                    ? (suggestedInvoicesQuery.data?.data ?? [])
                    : (invoicesQuery.data?.data ?? []);
                items = invoices.map((invoice) =>
                    invoiceToMatchableItem(
                        invoice,
                        currentTxLineItemMatchAmounts,
                    ),
                );
                break;
            }
            case MatchItemType.BILL: {
                const bills = isSuggestedMode
                    ? (suggestedBillsQuery.data?.data ?? [])
                    : (billsQuery.data?.data ?? []);
                items = bills.map((bill) =>
                    billToMatchableItem(bill, currentTxLineItemMatchAmounts),
                );
                break;
            }
        }

        return items.filter((item) => !selectedItemIds.has(item.id));
    }, [
        searchType,
        isSuggestedMode,
        transactionsQuery.data,
        invoicesQuery.data,
        billsQuery.data,
        activeSuggestedTransactionsQuery.data,
        suggestedInvoicesQuery.data,
        suggestedBillsQuery.data,
        selectedItemIds,
        currentTxLineItemMatchAmounts,
        isCashBasis,
    ]);

    const { totalCount, pageCount, isLoading } = useMemo(() => {
        switch (searchType) {
            case MatchItemType.TRANSACTION:
                return {
                    totalCount: isSuggestedMode
                        ? (activeSuggestedTransactionsQuery.data?.total ?? 0)
                        : (transactionsQuery.data?.total ?? 0),
                    pageCount: isSuggestedMode
                        ? (activeSuggestedTransactionsQuery.data?.pageCount ??
                          1)
                        : (transactionsQuery.data?.pageCount ?? 1),
                    isLoading: isSuggestedMode
                        ? activeSuggestedTransactionsQuery.isLoading ||
                          activeSuggestedTransactionsQuery.isFetching
                        : transactionsQuery.isLoading ||
                          transactionsQuery.isFetching,
                };
            case MatchItemType.INVOICE:
                return {
                    totalCount: isSuggestedMode
                        ? (suggestedInvoicesQuery.data?.total ?? 0)
                        : (invoicesQuery.data?.total ?? 0),
                    pageCount: isSuggestedMode
                        ? (suggestedInvoicesQuery.data?.pageCount ?? 1)
                        : (invoicesQuery.data?.pageCount ?? 1),
                    isLoading: isSuggestedMode
                        ? suggestedInvoicesQuery.isLoading ||
                          suggestedInvoicesQuery.isFetching
                        : invoicesQuery.isLoading || invoicesQuery.isFetching,
                };
            case MatchItemType.BILL:
                return {
                    totalCount: isSuggestedMode
                        ? (suggestedBillsQuery.data?.total ?? 0)
                        : (billsQuery.data?.total ?? 0),
                    pageCount: isSuggestedMode
                        ? (suggestedBillsQuery.data?.pageCount ?? 1)
                        : (billsQuery.data?.pageCount ?? 1),
                    isLoading: isSuggestedMode
                        ? suggestedBillsQuery.isLoading ||
                          suggestedBillsQuery.isFetching
                        : billsQuery.isLoading || billsQuery.isFetching,
                };
            default:
                return { totalCount: 0, pageCount: 1, isLoading: false };
        }
    }, [
        searchType,
        isSuggestedMode,
        transactionsQuery.data,
        transactionsQuery.isLoading,
        transactionsQuery.isFetching,
        invoicesQuery.data,
        invoicesQuery.isLoading,
        invoicesQuery.isFetching,
        billsQuery.data,
        billsQuery.isLoading,
        billsQuery.isFetching,
        activeSuggestedTransactionsQuery.data,
        activeSuggestedTransactionsQuery.isLoading,
        activeSuggestedTransactionsQuery.isFetching,
        suggestedInvoicesQuery.data,
        suggestedInvoicesQuery.isLoading,
        suggestedInvoicesQuery.isFetching,
        suggestedBillsQuery.data,
        suggestedBillsQuery.isLoading,
        suggestedBillsQuery.isFetching,
    ]);

    const handleMatch = useCallback(
        (item: MatchableItem) => {
            hasUserInteractedRef.current = true;
            addItem(item);
        },
        [addItem],
    );

    const emptyView = useCallback(() => {
        const pluralNoun = pluralize(searchType.toLowerCase(), 2);
        if (isSuggestedMode) {
            return (
                <EmptyState
                    header={`No suggested ${pluralNoun}`}
                    ctaText={`View all ${pluralNoun}`}
                    action={() =>
                        handleSearchModeChange(AccrualMatchSearchMode.ALL)
                    }
                />
            );
        }
        return <EmptyState header={`No ${pluralNoun}`} />;
    }, [isSuggestedMode, searchType, handleSearchModeChange]);

    const footerState = useMemo<AccrualMatchSearchFooterState>(
        () => ({
            totalCount,
            isLoading,
            pageCount,
            page,
            onPageChange: handlePageChange,
            shouldShowPagination:
                isInvoiceTransactionSuggestions ||
                !(isSuggestedMode && searchType === MatchItemType.TRANSACTION),
        }),
        [
            totalCount,
            isLoading,
            pageCount,
            page,
            handlePageChange,
            isSuggestedMode,
            searchType,
            isInvoiceTransactionSuggestions,
        ],
    );

    return (
        <>
            <TablePageHeader
                search={filters.search}
                onSearchChange={(value) =>
                    handleFiltersChange({ search: value ?? "" })
                }
                filters={filters}
                onFiltersChange={handleFiltersChange}
                defaultFilters={{}}
                hideClearButton
                availableFilters={availableFilters}
                disabled={false}
                isFullPage
                prependChildren={
                    isSearchModePickerVisible && (
                        <Picker.Select
                            items={SEARCH_MODE_OPTIONS}
                            value={searchMode}
                            onValueChange={handleSearchModeChange}
                        />
                    )
                }
            >
                <EntityFilterCombobox
                    filters={entityFilterValue}
                    onChange={handleFiltersChange}
                    onlyBusiness
                    disabled={isEntityFilterPinned}
                />
                {searchMatchTypeOptions.length > 1 && (
                    <Picker.Select
                        items={searchMatchTypeOptions}
                        value={searchType}
                        onValueChange={handleTypeChange}
                    />
                )}
                <AccrualMatchColumnSettings
                    columnsVariant="workspaceMatched"
                    matchItemType={searchType}
                />
            </TablePageHeader>
            <div className={tableStyles.flexFill}>
                <AccrualMatchTable
                    className={tableStyles.insetTable}
                    data={data}
                    section="search"
                    onAction={handleMatch}
                    emptyView={emptyView}
                    matchItemType={searchType}
                    sort={isRankedSearch ? undefined : sort}
                    onSortChange={isRankedSearch ? undefined : handleSortChange}
                />
            </div>
            {renderFooter(footerState)}
        </>
    );
};
